import React, { useState, useEffect } from 'react';
import { parseResume } from '../utils/resumeParser';
import { callAI } from '../services/aiApi';
import { showAlert } from '../utils/swal';

export default function Profile() {
    const [user, setUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        location: '',
        company: '',
        skills: '',
        experience: '',
        education: '',
        summary: '',
        linkedin: '',
        portfolio: '',
        resume: '',
        professionalTitle: '',
        experienceLevel: '',
        preferredRole: '',
        preferredLocation: '',
        expectedSalary: '',
        workMode: '',
        availability: ''
    });
    const [isParsing, setIsParsing] = useState(false);
    const [isGeneratingATS, setIsGeneratingATS] = useState(false);
    const [isOptimizingSummary, setIsOptimizingSummary] = useState(false);
    const [showAtsModal, setShowAtsModal] = useState(false);
    const [atsHtml, setAtsHtml] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('token');
        const fetchLatestProfile = async () => {
            if (!token) return;
            try {
                const res = await fetch((process.env.REACT_APP_API_URL || '') + '/api/auth/me', {
                    headers: { 'Authorization': `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.user) {
                        localStorage.setItem('user', JSON.stringify(data.user));
                        setUser(data.user);
                        setFormData({
                            name: data.user.name || '',
                            email: data.user.email || '',
                            phone: data.user.phone || '',
                            location: data.user.location || '',
                            company: data.user.company || '',
                            skills: data.user.skills || '',
                            experience: data.user.experience || '',
                            education: data.user.education || '',
                            summary: data.user.summary || '',
                            linkedin: data.user.linkedin || '',
                            portfolio: data.user.portfolio || '',
                            resume: data.user.resume || '',
                            professionalTitle: data.user.professionalTitle || '',
                            experienceLevel: data.user.experienceLevel || '',
                            preferredRole: data.user.preferredRole || '',
                            preferredLocation: data.user.preferredLocation || '',
                            expectedSalary: data.user.expectedSalary || '',
                            workMode: data.user.workMode || '',
                            availability: data.user.availability || ''
                        });
                        return;
                    }
                }
            } catch (err) {
                console.error("Error fetching latest profile:", err);
            }
        };

        const storedUser = localStorage.getItem('user');
        if (storedUser && storedUser !== 'undefined') {
            const parsed = JSON.parse(storedUser);
            setUser(parsed);
            setFormData({
                name: parsed.name || '',
                email: parsed.email || '',
                phone: parsed.phone || '',
                location: parsed.location || '',
                company: parsed.company || '',
                skills: parsed.skills || '',
                experience: parsed.experience || '',
                education: parsed.education || '',
                summary: parsed.summary || '',
                linkedin: parsed.linkedin || '',
                portfolio: parsed.portfolio || '',
                resume: parsed.resume || '',
                professionalTitle: parsed.professionalTitle || '',
                experienceLevel: parsed.experienceLevel || '',
                preferredRole: parsed.preferredRole || '',
                preferredLocation: parsed.preferredLocation || '',
                expectedSalary: parsed.expectedSalary || '',
                workMode: parsed.workMode || '',
                availability: parsed.availability || ''
            });
        }

        fetchLatestProfile();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsParsing(true);
        try {
            // 1. Upload file to backend
            const uploadData = new FormData();
            uploadData.append('file', file);
            const token = localStorage.getItem('token');

            const uploadRes = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: uploadData
            });

            if (!uploadRes.ok) {
                const errorText = await uploadRes.text();
                console.error('Upload failed:', uploadRes.status, errorText);
                try {
                    const errorJson = JSON.parse(errorText);
                    throw new Error(`Server Error: ${errorJson.error || errorText}`);
                } catch (e) {
                    if (e.message.startsWith('Server Error:')) throw e;
                    throw new Error(`Server Error: ${errorText}`);
                }
            }
            const { url } = await uploadRes.json();

            // 2. Parse PDF client-side
            const parsedData = await parseResume(file);

            setFormData(prev => ({
                ...prev,
                resume: url, // Save resume URL
                email: parsedData.email || prev.email,
                phone: parsedData.phone || prev.phone,
                skills: parsedData.skills || prev.skills,
                experience: parsedData.experience || prev.experience,
                education: parsedData.education || prev.education,
                summary: parsedData.summary || prev.summary,
                linkedin: parsedData.links.find(l => l.includes('linkedin')) || prev.linkedin,
                portfolio: parsedData.links.find(l => !l.includes('linkedin')) || prev.portfolio
            }));
            showAlert('Success', 'Resume uploaded and parsed successfully!', 'success');
        } catch (error) {
            console.error('Resume upload/parse error:', error);
            let errorMessage = 'Failed to upload/parse resume. Please try again.';

            if (error.message.startsWith('Server Error:')) {
                errorMessage = error.message;
            } else if (error.message === 'File upload failed') {
                errorMessage = 'Failed to upload resume file. Please check your network or try a smaller file.';
            } else if (error.name === 'MissingPDFException') {
                errorMessage = 'Failed to parse PDF. The file might be corrupted or password protected.';
            }
            showAlert('Error', errorMessage, 'error');
        } finally {
            setIsParsing(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/auth/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (!res.ok) throw new Error('Failed to update profile');

            const data = await res.json();
            setUser(data.user);
            localStorage.setItem('user', JSON.stringify(data.user));
            setIsEditing(false);
            showAlert('Success', 'Profile updated successfully!', 'success');
        } catch (error) {
            console.error(error);
            showAlert('Error', 'Failed to update profile. Please try again.', 'error');
        }
    };

    const handleGenerateATS = async () => {
        if (!user) return;
        
        // Ensure minimum required data is present
        const hasMinimumData = formData.name && formData.email && (formData.skills || formData.experience || formData.education);
        
        if (!hasMinimumData) {
            return showAlert(
                'Profile Incomplete', 
                'Please fill in your Name, Email, and at least one of: Skills, Experience, or Education before generating a resume.', 
                'warning'
            );
        }

        setIsGeneratingATS(true);
        try {
            const result = await callAI('generateATSResume', formData);
            setAtsHtml(result); // The raw HTML
            setShowAtsModal(true);
        } catch (error) {
            console.error(error);
            showAlert('AI Error', 'Failed to generate ATS Resume. Try again.', 'error');
        } finally {
            setIsGeneratingATS(false);
        }
    };

    const handleOptimizeSummary = async () => {
        if (!formData.summary) {
            return showAlert('Missing Summary', 'Please write a brief summary first so AI can optimize it.', 'warning');
        }
        setIsOptimizingSummary(true);
        try {
            const result = await callAI('optimizeProfile', { profileText: formData.summary });
            if (result && result.optimizedSummary) {
                setFormData(prev => ({ ...prev, summary: result.optimizedSummary }));
                showAlert('Optimized', 'Your summary has been optimized by AI!', 'success');
            }
        } catch (error) {
            console.error(error);
            showAlert('AI Error', 'Failed to optimize summary.', 'error');
        } finally {
            setIsOptimizingSummary(false);
        }
    };

    const handlePrintATS = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>\${formData.name} - ATS Resume</title>
                    <style>
                        body { font-family: Arial, sans-serif; line-height: 1.6; color: #000; background: #fff; padding: 40px; margin: 0; }
                        * { box-sizing: border-box; }
                        h1, h2, h3 { color: #000; margin-bottom: 8px; }
                        ul { margin-top: 4px; padding-left: 20px; }
                        .resume-container { max-width: 800px; margin: 0 auto; }
                    </style>
                </head>
                <body>\${atsHtml}</body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        // optionally close printWindow after print, but usually browser print dialog blocks it
    };

    if (!user) return <div className="min-h-screen flex items-center justify-center bg-light text-dark">Loading...</div>;

    return (
        <div className="min-h-screen bg-light py-12 px-4 sm:px-6 lg:px-8 pt-32 text-body relative overflow-hidden">
            {/* Background Shapes */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[120px]" />
            </div>

            <div className="max-w-5xl mx-auto relative z-10">
                <div className="bg-white rounded-3xl shadow-card border border-gray-100 overflow-hidden">

                    {/* Header Banner */}
                    <div className="h-48 bg-gradient-to-r from-primary/10 to-blue-500/10 border-b border-gray-100 relative">
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                    </div>

                    <div className="px-8 pb-8">
                        {/* Avatar & Info */}
                        <div className="relative flex flex-col md:flex-row items-end -mt-16 mb-8 gap-6">
                            <div className="h-32 w-32 rounded-3xl bg-white p-2 shadow-card border border-gray-100">
                                <div className="h-full w-full rounded-2xl bg-primary flex items-center justify-center text-white text-4xl font-bold shadow-inner">
                                    {user.name.charAt(0).toUpperCase()}
                                </div>
                            </div>
                            <div className="mb-2 flex-1">
                                <h1 className="text-3xl font-bold text-dark mb-1">{user.name}</h1>
                                {user.role === 'jobseeker' && user.professionalTitle && (
                                    <p className="text-secondary font-bold text-lg mb-2">{user.professionalTitle}</p>
                                )}
                                <p className="text-primary text-sm capitalize font-bold bg-primary/10 px-4 py-1.5 rounded-full inline-block">{user.role}</p>
                            </div>
                            <div className="mb-2">
                                {!isEditing && (
                                    <div className="flex gap-2">
                                        {user.role === 'jobseeker' && (
                                            <button
                                                onClick={handleGenerateATS}
                                                disabled={isGeneratingATS}
                                                className={`px-6 py-3 border border-transparent text-sm font-bold rounded-xl transition-all shadow-sm flex items-center gap-2 ${isGeneratingATS ? 'bg-gray-200 text-gray-500' : 'bg-primary/10 text-primary hover:bg-primary/20 hover:-translate-y-0.5'}`}
                                            >
                                                {isGeneratingATS ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-magic"></i>} 
                                                {isGeneratingATS ? 'Generating ATS...' : 'Generate ATS Resume ✨'}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setIsEditing(true)}
                                            className="px-6 py-3 bg-white text-dark border border-gray-200 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all shadow-sm hover:-translate-y-0.5"
                                        >
                                            Edit Profile
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Content */}
                        {isEditing ? (
                            <form onSubmit={handleSubmit} className="space-y-8 animate-fade-in-up">
                                {/* Resume Upload Section */}
                                {user.role === 'jobseeker' && (
                                    <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6">
                                        <h3 className="text-primary font-bold mb-2 flex items-center gap-2"><i className="fas fa-rocket"></i> AI Resume Parser</h3>
                                        <p className="text-sm text-body mb-4">Upload your resume (PDF) to autofill your profile details instantly.</p>
                                        <div className="relative">
                                            <input
                                                type="file"
                                                accept=".pdf"
                                                onChange={handleFileUpload}
                                                className="block w-full text-sm text-body file:mr-4 file:py-2.5 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-primary file:text-white hover:file:bg-blue-700 cursor-pointer"
                                                disabled={isParsing}
                                            />
                                            {isParsing && (
                                                <div className="absolute top-0 right-0 flex items-center gap-2 text-primary text-sm font-medium">
                                                    <span className="animate-spin"><i className="fas fa-spinner"></i></span> Parsing...
                                                </div>
                                            )}
                                        </div>
                                        {formData.resume && (
                                            <div className="mt-4">
                                                <a href={formData.resume} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-2 font-bold">
                                                    <i className="fas fa-file-pdf"></i> View Uploaded Resume
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="md:col-span-2">
                                        <h3 className="text-lg font-bold text-dark mb-4 border-b border-gray-100 pb-2">Personal Information</h3>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-dark mb-2">Full Name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-dark mb-2">Email Address</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            disabled
                                            className="w-full px-5 py-3.5 bg-gray-100 border border-gray-200 rounded-xl text-gray-500 cursor-not-allowed"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-dark mb-2">Phone Number</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="+1 (555) 000-0000"
                                            className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-dark mb-2">Location</label>
                                        <input
                                            type="text"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                            placeholder="City, Country"
                                            className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                        />
                                    </div>

                                    {user.role === 'jobseeker' && (
                                        <>
                                            <div>
                                                <label className="block text-sm font-bold text-dark mb-2">Professional Title</label>
                                                <input
                                                    type="text"
                                                    name="professionalTitle"
                                                    value={formData.professionalTitle}
                                                    onChange={handleChange}
                                                    placeholder="e.g. Product Manager | 5+ Years in FinTech"
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-dark mb-2">Experience Level</label>
                                                <input
                                                    type="text"
                                                    name="experienceLevel"
                                                    value={formData.experienceLevel}
                                                    onChange={handleChange}
                                                    placeholder="e.g. Mid-Level, Senior"
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                                />
                                            </div>
                                        </>
                                    )}

                                    {user.role === 'employer' && (
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-bold text-dark mb-2">Company Name</label>
                                            <input
                                                type="text"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleChange}
                                                className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                            />
                                        </div>
                                    )}

                                    {user.role === 'jobseeker' && (
                                        <>
                                            <div className="md:col-span-2 mt-6">
                                                <h3 className="text-lg font-bold text-dark mb-4 border-b border-gray-100 pb-2">Professional Details</h3>
                                            </div>

                                            <div className="md:col-span-2">
                                                <div className="flex justify-between items-center mb-2">
                                                    <label className="block text-sm font-bold text-dark">Professional Summary</label>
                                                    <button
                                                        type="button"
                                                        onClick={handleOptimizeSummary}
                                                        disabled={isOptimizingSummary || !formData.summary}
                                                        className="text-sm font-semibold text-primary hover:text-primary-dark transition-colors flex items-center gap-1 disabled:opacity-50"
                                                    >
                                                        {isOptimizingSummary ? '✨ Optimizing...' : '✨ AI Optimize'}
                                                    </button>
                                                </div>
                                                <textarea
                                                    name="summary"
                                                    value={formData.summary}
                                                    onChange={handleChange}
                                                    rows="3"
                                                    placeholder="Brief summary of your career..."
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                                />
                                            </div>

                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-bold text-dark mb-2">Skills</label>
                                                <input
                                                    type="text"
                                                    name="skills"
                                                    value={formData.skills}
                                                    onChange={handleChange}
                                                    placeholder="e.g. React, Node.js, Design"
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                                />
                                            </div>

                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-bold text-dark mb-2">Work Experience</label>
                                                <textarea
                                                    name="experience"
                                                    value={formData.experience}
                                                    onChange={handleChange}
                                                    rows="5"
                                                    placeholder="Describe your work experience..."
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                                />
                                            </div>

                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-bold text-dark mb-2">Education</label>
                                                <textarea
                                                    name="education"
                                                    value={formData.education}
                                                    onChange={handleChange}
                                                    rows="3"
                                                    placeholder="Degrees, Universities, Years..."
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                                />
                                            </div>

                                            <div className="md:col-span-2 mt-6">
                                                <h3 className="text-lg font-bold text-dark mb-4 border-b border-gray-100 pb-2">Career Preferences</h3>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-bold text-dark mb-2">Preferred Job Role</label>
                                                <input
                                                    type="text"
                                                    name="preferredRole"
                                                    value={formData.preferredRole}
                                                    onChange={handleChange}
                                                    placeholder="e.g. Senior Product Manager"
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-dark mb-2">Preferred Location</label>
                                                <input
                                                    type="text"
                                                    name="preferredLocation"
                                                    value={formData.preferredLocation}
                                                    onChange={handleChange}
                                                    placeholder="e.g. Remote, Mumbai"
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-dark mb-2">Expected Salary</label>
                                                <input
                                                    type="text"
                                                    name="expectedSalary"
                                                    value={formData.expectedSalary}
                                                    onChange={handleChange}
                                                    placeholder="e.g. ₹25 - ₹35 LPA"
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-dark mb-2">Work Mode</label>
                                                <select
                                                    name="workMode"
                                                    value={formData.workMode}
                                                    onChange={handleChange}
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white cursor-pointer"
                                                >
                                                    <option value="">Select Work Mode</option>
                                                    <option value="Remote">Remote</option>
                                                    <option value="Hybrid">Hybrid</option>
                                                    <option value="On-site">On-site</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-dark mb-2">Availability</label>
                                                <select
                                                    name="availability"
                                                    value={formData.availability}
                                                    onChange={handleChange}
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white cursor-pointer"
                                                >
                                                    <option value="">Select Availability</option>
                                                    <option value="Immediate">Immediate</option>
                                                    <option value="30 days">30 days</option>
                                                    <option value="60 days">60 days</option>
                                                </select>
                                            </div>

                                            <div className="md:col-span-2 mt-6">
                                                <h3 className="text-lg font-bold text-dark mb-4 border-b border-gray-100 pb-2">Social Links</h3>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-bold text-dark mb-2">LinkedIn URL</label>
                                                <input
                                                    type="url"
                                                    name="linkedin"
                                                    value={formData.linkedin}
                                                    onChange={handleChange}
                                                    placeholder="https://linkedin.com/in/..."
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-dark mb-2">Portfolio / Website</label>
                                                <input
                                                    type="url"
                                                    name="portfolio"
                                                    value={formData.portfolio}
                                                    onChange={handleChange}
                                                    placeholder="https://..."
                                                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-dark placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all hover:bg-white"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
                                    <button
                                        type="button"
                                        onClick={() => setIsEditing(false)}
                                        className="px-6 py-3 text-body font-bold hover:text-dark transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-8 py-3 bg-primary text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-button hover:-translate-y-0.5"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                <div className="md:col-span-1 space-y-8">
                                    <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
                                        <h3 className="text-xs font-bold text-secondary uppercase tracking-wider mb-4">Contact Info</h3>
                                        <div className="space-y-4 text-sm">
                                            <div className="flex items-center gap-3 text-body">
                                                <span className="w-6 text-center text-lg text-primary"><i className="fas fa-envelope"></i></span>
                                                <span className="truncate font-medium">{user.email}</span>
                                            </div>
                                            {formData.phone && (
                                                <div className="flex items-center gap-3 text-body">
                                                    <span className="w-6 text-center text-lg text-primary"><i className="fas fa-phone"></i></span>
                                                    <span className="font-medium">{formData.phone}</span>
                                                </div>
                                            )}
                                            {formData.location && (
                                                <div className="flex items-center gap-3 text-body">
                                                    <span className="w-6 text-center text-lg text-primary"><i className="fas fa-map-marker-alt"></i></span>
                                                    <span className="font-medium">{formData.location}</span>
                                                </div>
                                            )}
                                            {user.company && (
                                                <div className="flex items-center gap-3 text-body">
                                                    <span className="w-6 text-center text-lg text-primary"><i className="fas fa-building"></i></span>
                                                    <span className="font-medium">{user.company}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {user.role === 'jobseeker' && (formData.preferredRole || formData.preferredLocation || formData.expectedSalary || formData.workMode || formData.availability) && (
                                        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
                                            <h3 className="text-xs font-bold text-secondary uppercase tracking-wider mb-4">Career Preferences</h3>
                                            <div className="space-y-4 text-sm">
                                                {formData.preferredRole && (
                                                    <div className="flex justify-between gap-2">
                                                        <span className="text-gray-400">Role</span>
                                                        <span className="font-bold text-dark text-right">{formData.preferredRole}</span>
                                                    </div>
                                                )}
                                                {formData.preferredLocation && (
                                                    <div className="flex justify-between gap-2">
                                                        <span className="text-gray-400">Location</span>
                                                        <span className="font-bold text-dark text-right">{formData.preferredLocation}</span>
                                                    </div>
                                                )}
                                                {formData.expectedSalary && (
                                                    <div className="flex justify-between gap-2">
                                                        <span className="text-gray-400">Salary</span>
                                                        <span className="font-bold text-dark text-right">{formData.expectedSalary}</span>
                                                    </div>
                                                )}
                                                {formData.workMode && (
                                                    <div className="flex justify-between gap-2">
                                                        <span className="text-gray-400">Mode</span>
                                                        <span className="font-bold text-dark text-right">{formData.workMode}</span>
                                                    </div>
                                                )}
                                                {formData.availability && (
                                                    <div className="flex justify-between gap-2">
                                                        <span className="text-gray-400">Availability</span>
                                                        <span className="font-bold text-dark text-right">{formData.availability}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="md:col-span-2 space-y-8">
                                    {user.role === 'jobseeker' && (
                                        <>
                                            {formData.summary && (
                                                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                                                    <h3 className="text-xl font-bold text-dark mb-4">About</h3>
                                                    <p className="text-body leading-relaxed">{formData.summary}</p>
                                                </div>
                                            )}

                                            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                                                <h3 className="text-xl font-bold text-dark mb-6">Skills</h3>
                                                <div className="flex flex-wrap gap-3">
                                                    {formData.skills ? (
                                                        formData.skills.split(',').map((skill, i) => (
                                                            <span key={i} className="px-4 py-2 bg-light text-secondary rounded-xl text-sm font-bold border border-gray-200 hover:border-primary hover:text-primary transition-colors cursor-default">
                                                                {skill.trim()}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="text-gray-400 text-sm italic">No skills added yet</span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                                                <h3 className="text-xl font-bold text-dark mb-6">Experience</h3>
                                                <div className="text-body whitespace-pre-line leading-relaxed">
                                                    {formData.experience || <span className="text-gray-400 italic">No experience added yet</span>}
                                                </div>
                                            </div>

                                            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                                                <h3 className="text-xl font-bold text-dark mb-6">Education</h3>
                                                <div className="text-body whitespace-pre-line leading-relaxed">
                                                    {formData.education || <span className="text-gray-400 italic">No education added yet</span>}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ATS Modal */}
            {showAtsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-3xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-fade-in-up">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h2 className="text-xl font-bold text-dark flex items-center gap-2"><i className="fas fa-file-alt text-primary"></i> AI Generated ATS Resume</h2>
                            <div className="flex gap-2">
                                <button onClick={handlePrintATS} className="px-4 py-2 bg-dark text-white font-bold rounded-xl hover:bg-black transition-all text-sm flex items-center gap-2">
                                    <i className="fas fa-print"></i> Print / Save PDF
                                </button>
                                <button onClick={() => setShowAtsModal(false)} className="px-4 py-2 bg-gray-200 text-dark font-bold rounded-xl hover:bg-gray-300 transition-all text-sm">
                                    Close
                                </button>
                            </div>
                        </div>
                        <div className="p-8 overflow-y-auto bg-white flex-1" style={{fontFamily: 'Arial, sans-serif', color: 'black'}}>
                            <div dangerouslySetInnerHTML={{ __html: atsHtml }} className="prose max-w-none text-black"></div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
